
import type {
  LearningAnswerOption,
  LearningContentItem,
  LearningConversationItem,
} from '@services/firebase/learning'
import {
  getLearningLocaleFallbacks,
  resolveLocalizedDisplayText,
  resolveLocalizedMapText,
  resolveTranslatedText,
} from '@features/learning/shared/learning-helpers'
import type { LegacySelfStudyTemplateId } from '../self-study-template-contract'

type SelfStudyAnswerPayload =
  | Array<LearningAnswerOption | string>
  | LearningAnswerOption
  | string
  | undefined

export function resolveConversationText(
  conversation: LearningConversationItem,
  locale: string,
) {
  return (
    resolveTranslatedText(conversation.translates, locale) ??
    resolveLocalizedMapText(conversation.translate, locale) ??
    conversation.text ??
    conversation.answer ??
    ''
  )
}

export function normalizeAnswerOptions(answers: SelfStudyAnswerPayload) {
  if (!answers) {
    return []
  }

  const normalizedAnswers = Array.isArray(answers) ? answers : [answers]

  return normalizedAnswers
    .map((answer) => {
      if (typeof answer === 'string') {
        return answer
      }

      if (answer.status === false) {
        return null
      }

      return answer
    })
    .filter((answer): answer is LearningAnswerOption | string => answer !== null)
}

export function isGenericAnswerPlaceholder(label: string) {
  return /^answer\s+\d+$/i.test(label.trim())
}

function resolveLegacyTranslationAffordanceText(
  translate: LearningAnswerOption['translate'] | undefined,
  locale: string,
) {
  if (!translate) {
    return null
  }

  const seen = new Set<string>()

  for (const language of getLearningLocaleFallbacks(locale)) {
    const trimmed = translate[language]?.trim()

    if (!trimmed) {
      continue
    }

    // `Answer N` is the legacy label for an audio-only option, not a translation.
    if (isGenericAnswerPlaceholder(trimmed)) {
      continue
    }

    if (seen.has(trimmed)) {
      continue
    }

    seen.add(trimmed)
    return trimmed
  }

  return null
}

export function resolveLegacyAnswerOptionPayloadLabel(
  option: LearningAnswerOption | string,
  locale: string,
  options?: { readonly suppressGenericPlaceholder?: boolean },
) {
  const candidates = typeof option === 'string'
    ? [option]
    : [
      option.answer,
      option.text,
      resolveLocalizedDisplayText(option.label, locale),
      resolveLocalizedDisplayText(option.title, locale),
      resolveLocalizedDisplayText(option.content, locale),
      option.value,
      resolveLocalizedMapText(option.translate, locale),
    ]
  const trimmedCandidates = candidates
    .map((candidate) => candidate?.trim() ?? '')
    .filter(Boolean)
  const trimmedLabel =
    options?.suppressGenericPlaceholder
      ? trimmedCandidates.find((candidate) => !isGenericAnswerPlaceholder(candidate)) ??
        trimmedCandidates[0] ??
        ''
      : trimmedCandidates[0] ?? ''

  if (
    options?.suppressGenericPlaceholder &&
    isGenericAnswerPlaceholder(trimmedLabel)
  ) {
    return ''
  }

  return trimmedLabel
}

interface LegacyMatchPair {
  readonly answerLabel: string
  readonly targetLabel: string
}

export interface LegacyMatchChoice {
  readonly id: string
  readonly label: string
}

export interface LegacyMatchTarget {
  readonly answerLabel: string
  readonly id: string
  readonly placedChoice: LegacyMatchChoice | null
  readonly targetLabel: string
}

export function resolveLegacyMatchPair(
  option: LearningAnswerOption | string,
  locale: string,
): LegacyMatchPair {
  if (typeof option === 'string') {
    const label = option.trim()
    return {
      answerLabel: label,
      targetLabel: label,
    }
  }

  const answerLabel = (
    option.answer ??
    option.text ??
    option.value ??
    resolveLocalizedDisplayText(option.label, locale) ??
    resolveLocalizedDisplayText(option.title, locale) ??
    resolveLocalizedDisplayText(option.content, locale) ??
    resolveLocalizedMapText(option.translate, locale) ??
    ''
  ).trim()
  const targetLabel = (
    resolveLocalizedMapText(option.translate, locale) ??
    option.text ??
    option.answer ??
    answerLabel
  ).trim()

  return {
    answerLabel,
    targetLabel,
  }
}

export function createLegacyMatchChoices(
  pairs: readonly LegacyMatchPair[],
  itemId: string,
) {
  return shuffleItems(
    pairs.map((pair, index) => ({
      id: `${itemId}-choice-${index}-${pair.answerLabel}`,
      label: pair.answerLabel,
    })),
  )
}

export function createLegacyMatchTargets(
  pairs: readonly LegacyMatchPair[],
  itemId: string,
): LegacyMatchTarget[] {
  return pairs.map((pair, index) => ({
    answerLabel: pair.answerLabel,
    id: `${itemId}-target-${index}-${pair.answerLabel}-${pair.targetLabel}`,
    placedChoice: null,
    targetLabel: pair.targetLabel,
  }))
}

export function hasScoredAnswers(answers: Array<LearningAnswerOption | string>) {
  return answers.some((answer) => {
    if (typeof answer === 'string') {
      return false
    }

    return Boolean(answer.correct ?? answer.isRight ?? answer.result)
  })
}

export function resolveAnswerResult(answer: LearningAnswerOption | string) {
  if (typeof answer === 'string') {
    return false
  }

  return Boolean(answer.correct ?? answer.isRight ?? answer.result)
}

export function resolveLegacyCorrectAnswerReviewText(
  item: LearningContentItem,
  answers: readonly (LearningAnswerOption | string)[],
  locale: string,
  template: LegacySelfStudyTemplateId | null,
  fallbackAnswers: readonly string[],
) {
  const note = resolveLocalizedMapText(item.note, locale)

  if (note) {
    return note
  }

  const correctAnswers = answers
    .map((answer, index) => ({ answer, index }))
    .filter(({ answer }) => typeof answer === 'string' || resolveAnswerResult(answer))

  if (template === '1') {
    const correctAnswer = correctAnswers[0]

    if (!correctAnswer) {
      return fallbackAnswers[0] ?? null
    }

    const correctOption = correctAnswer.answer
    const label = resolveLegacyAnswerOptionPayloadLabel(correctOption, locale, {
      suppressGenericPlaceholder: true,
    })

    if (
      typeof correctOption !== 'string' &&
      correctOption.audio &&
      !label &&
      !correctOption.image
    ) {
      return `Answer ${correctAnswer.index + 1}`
    }

    return label || fallbackAnswers[0] || null
  }

  const correctLabels = correctAnswers
    .map(({ answer }) => resolveLegacyAnswerOptionPayloadLabel(answer, locale, {
      suppressGenericPlaceholder: true,
    }))
    .filter(Boolean)

  return correctLabels.length > 0
    ? correctLabels.join(' / ')
    : fallbackAnswers[0] ?? null
}

export function resolveLegacyCorrectAnswerReviewTranslationText(
  item: LearningContentItem,
  answers: readonly (LearningAnswerOption | string)[],
  locale: string,
  template: LegacySelfStudyTemplateId | null,
) {
  const note = resolveLocalizedMapText(item.note, locale)

  if (note) {
    return null
  }

  const indexedAnswers = answers.map((answer, index) => ({ answer, index }))
  const correctAnswers = indexedAnswers.filter(({ answer }) =>
    typeof answer !== 'string' && resolveAnswerResult(answer),
  )
  const candidateAnswers =
    template === '1'
      ? correctAnswers.slice(0, 1)
      : correctAnswers.length > 0
        ? correctAnswers
        : indexedAnswers.filter(({ answer }) => typeof answer !== 'string')

  for (const { answer } of candidateAnswers) {
    if (typeof answer === 'string') {
      continue
    }

    const translation = resolveLegacyTranslationAffordanceText(
      answer.translate,
      locale,
    )

    if (translation) {
      return translation
    }
  }

  return null
}

export function formatElapsedDuration(seconds: number) {
  const roundedSeconds = Math.max(0, Math.round(seconds))
  const minutes = Math.floor(roundedSeconds / 60)
  const remainingSeconds = roundedSeconds % 60

  if (minutes === 0) {
    return `${remainingSeconds}s`
  }

  return `${minutes}m ${remainingSeconds.toString().padStart(2, '0')}s`
}

function normalizePracticeAnswer(value: string) {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

export function comparePracticeAnswer(actual: string, expectedAnswers: readonly string[]) {
  const normalizedActual = normalizePracticeAnswer(actual)

  if (!normalizedActual) {
    return false
  }

  return expectedAnswers.some((expectedAnswer) => {
    const normalizedExpected = normalizePracticeAnswer(expectedAnswer)

    if (!normalizedExpected) {
      return false
    }

    return (
      normalizedExpected === normalizedActual ||
      normalizedExpected.includes(normalizedActual) ||
      normalizedActual.includes(normalizedExpected)
    )
  })
}

export function matchesLegacyWriteAnswer(actual: string, expectedAnswers: readonly string[]) {
  return expectedAnswers.some((expectedAnswer) =>
    expectedAnswer.toLocaleLowerCase() === actual.toLocaleLowerCase(),
  )
}

function normalizeLegacySpeechAnswer(value: string) {
  return value
    .toLocaleLowerCase()
    .replace(/[$@%!?,.]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

export function matchesLegacySpeechAnswer(actual: string, expectedAnswers: readonly string[]) {
  const normalizedActual = normalizeLegacySpeechAnswer(actual)

  if (!normalizedActual) {
    return false
  }

  return expectedAnswers.some((expectedAnswer) =>
    normalizeLegacySpeechAnswer(expectedAnswer) === normalizedActual,
  )
}

export function shuffleItems<Item>(items: readonly Item[]) {
  const nextItems = [...items]

  for (let index = nextItems.length - 1; index > 0; index -= 1) {
    const targetIndex = Math.floor(Math.random() * (index + 1))
    const currentItem = nextItems[index]
    nextItems[index] = nextItems[targetIndex]
    nextItems[targetIndex] = currentItem
  }

  return nextItems
}

export function shuffleTokens(tokens: readonly string[]) {
  return shuffleItems(tokens)
}

export function splitAnswerTokens(answer: string, mode: 'letters' | 'words') {
  if (!answer.trim()) {
    return []
  }

  if (mode === 'letters') {
    return answer.replace(/\s+/g, '').split('')
  }

  return answer.trim().split(/\s+/)
}

